import { useEffect, useState, useCallback, useRef } from 'react';
import { track, EVENTS } from '@/lib/mixpanel';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useTranslation } from 'react-i18next';
import { getLanguagePath } from '../lib/utils';
import { MetaHead } from "@/components/common/MetaHead";
import { HelpSection } from "@/components/common/HelpSection";
import { OrderInfoSection } from "@/components/common/OrderInfoSection";
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { trackPurchase as trackMetaPurchase, isBrowser } from '@/lib/meta';
import { trackPurchase as trackGa4Purchase } from '@/lib/gtag';
import { IconChevronDown } from '@tabler/icons-react';
import { isZeroDecimalCurrency } from '@/lib/currencies';

const APP_STORE_URL = 'https://apps.apple.com/app/id6469414539';
const API_URL = import.meta.env.VITE_WORKER_URL || '';

export interface StripeProduct {
  name: string;
  description: string | null;
  metadata: Record<string, string>;
}

export interface ReceiptData {
  url?: string;
  product?: StripeProduct;
  invoiceId?: string;
  metadata?: {
    app_user_id?: string;
    user_email?: string;
    login_provider?: string;
    promotion_code?: string; // backward compatability
    event_code?: string;
    referral_code?: string;
  };
  customer?: {
    id?: string;
    email?: string;
  };
  payment?: {
    amount_total?: number;
    currency?: string;
  };
  checkoutMode?: 'login_required' | 'login_optional';
  sessionId?: string;
}

export default function Success() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [receipt, setReceipt] = useState<ReceiptData>();
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRedirectingRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [showAppStoreQrCode, setShowAppStoreQrCode] = useState(false);
  const [downloadClicked, setDownloadClicked] = useState(false);

  const redirectToSignIn = useCallback(() => {
    if (isRedirectingRef.current) {
      console.log('Already redirecting, skipping');
      return;
    }
    isRedirectingRef.current = true;
    const currentPath = window.location.pathname + window.location.search;
    console.log('currentPath', currentPath);
    localStorage.setItem('authRedirectTo', currentPath);
    localStorage.removeItem('pendingPurchasePlanId');
    navigate(getLanguagePath('sign-in'));
  }, [navigate]);

  // Handle auth check and initial page view tracking
  useEffect(() => {
    if (loading) {
      console.log('Auth still loading...');
      return; // Wait for auth to finish loading
    }

    // Only redirect to sign-in if we don't have receipt data yet
    // In login-optional mode, we can show the page without authentication
    if (!user && !receipt) {
      console.log('No user and no receipt data, will check receipt first');
      // Don't redirect immediately, let the receipt fetch determine the checkout mode
    } else if (user) {
      // Track page view when user is authenticated
      track(EVENTS.VIEW_SUCCESS_PAGE, {
        sessionId,
      });
    }
  }, [user, loading, redirectToSignIn, sessionId, receipt]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get current session for auth token
        const { data: { session: authSession } } = await supabase.auth.getSession();

        // First, try to fetch receipt data to determine checkout mode
        const headers: Record<string, string> = {};
        if (authSession?.access_token) {
          headers['Authorization'] = `Bearer ${authSession.access_token}`;
        }

        const receiptRes = await fetch(`${API_URL}/api/get-receipt?session_id=${sessionId}`, {
          headers
        });

        if (!receiptRes.ok) {
          if (receiptRes.status === 401 || receiptRes.status === 403) {
            // If we get auth error, check if we have no auth session
            if (!authSession?.access_token && !isRedirectingRef.current) {
              console.log('No auth session, redirecting to sign in');
              redirectToSignIn();
              return;
            }
            redirectToSignIn();
            return;
          }
          throw new Error('Failed to fetch receipt');
        }

        const receiptData = await receiptRes.json();
        setReceipt(receiptData);

        // If this is login_required mode and we don't have a user, redirect to sign-in
        if (receiptData.checkoutMode === 'login_required' && !user && !isRedirectingRef.current) {
          console.log('Login required mode but no user, redirecting to sign in');
          redirectToSignIn();
          return;
        }

        // Track Purchase event on frontend (separate from backend webhook)
        // This provides browser-side data (fbp, fbc) that webhook can't access
        // Use session_id as event_id for deduplication, invoice_id as order_id for tracking
        const purchaseTrackedKey = `purchase_tracked_${sessionId}`;
        if (isBrowser() && receiptData?.payment?.amount_total && receiptData?.payment?.currency && sessionId && !localStorage.getItem(purchaseTrackedKey)) {
          localStorage.setItem(purchaseTrackedKey, 'true');
          const currency = receiptData.payment.currency.toLowerCase();
          const amount = isZeroDecimalCurrency(currency)
            ? receiptData.payment.amount_total
            : receiptData.payment.amount_total / 100;

          // Meta Purchase tracking
          trackMetaPurchase(
            amount,
            currency.toUpperCase(),
            sessionId, // Use session_id for event deduplication
            receiptData.invoiceId || sessionId // Use invoice_id for order tracking, fallback to session_id
          );

          // GA4 Purchase tracking
          trackGa4Purchase({
            transaction_id: sessionId,
            value: amount,
            currency: currency.toUpperCase(),
            items: [{
              item_id: receiptData.product?.metadata?.lookup_key || 'subscription',
              item_name: receiptData.product?.name || 'Subscription',
              price: amount,
              quantity: 1,
            }],
            ...(receiptData.metadata?.eventCode && { promotion_id: receiptData.metadata.eventCode }),
            ...(receiptData.metadata?.referralCode && { coupon: receiptData.metadata.referralCode }),
          });
        }

      } catch (error) {
        console.error('Error fetching receipt:', error);
        toast({
          variant: "destructive",
          title: t('common.error'),
          description: error instanceof Error ? error.message : 'An error occurred',
        });
      }
    }

    console.log('Receipt effect triggered - sessionId:', sessionId, 'user:', !!user, 'userEmail:', user?.email, 'loading:', loading, 'hasFetched:', hasFetchedRef.current);
    if (sessionId && !loading && !hasFetchedRef.current) {
      console.log('About to call fetchData');
      hasFetchedRef.current = true;
      localStorage.removeItem('pendingPurchasePlanId');
      fetchData();
    }
  }, [sessionId, user, loading]);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <MetaHead
        title={t('meta.success.title')}
        description={t('meta.success.description')}
      />
      {/* Success Header */}
      <div className="max-w-xl lg:max-w-6xl mx-auto">

        <div className="grid lg:grid-cols-12 gap-2 lg:gap-8 min-h-[600px]">

          {/* Left Column: Order Information and Help */}

          <div className="space-y-4 lg:col-span-6 lg:pt-8 flex flex-col">
            {/* Logo */}
            <div className="p-3">
              <Link to={getLanguagePath('/')}>
                <img
                  src="/brands/clarymind-logo_logotype-app-transparent-light-outline.svg"
                  alt="Clarymind"
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            {/* Info */}
            <div className="p-6 space-y-4">
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-8">
                {t('success.title')}
              </h1>
              <p className="text-base md:text-base text-slate-600">
                {t('success.subtitle')}
              </p>
              <p className="text-base md:text-base text-slate-600">
                {receipt?.checkoutMode === 'login_required' ? t('success.descriptionWithLogin') : t('success.descriptionWithoutLogin')}
              </p>
            </div>

            {/* Divider */}

            {receipt?.checkoutMode === 'login_required' && (
              <div className="hidden lg:block pb-8 pt-4 mx-5">
                <div className="h-[1px] w-full bg-slate-200" />
              </div>
            )}

            <div className="hidden lg:flex flex-1 flex-col justify-start gap-8">
              {/* Help Section */}
              <HelpSection />

              {/* Order Information */}
              <OrderInfoSection receipt={receipt} />
            </div>
          </div>

          {/* Right Column: Next Steps - Full width on mobile */}
          <div className="lg:col-span-6">
            <Card className="px-8 py-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] border-slate-200 rounded-xl h-full flex flex-col">

              {receipt?.checkoutMode === 'login_optional' && (
                <h2 className="text-lg md:text-xl font-semibold border-l-4 border-sky-500 pl-4 py-0 mt-4 lg:mt-6 mb-6 lg:mb-10 text-slate-900">{t('success.howToActivate')}</h2>
              )}

              {/* Download App */}
              <div className="grid lg:grid-cols-12 gap-6 items-center mt-4 mb-10">

                {/* QR Code - right for login_required, left for login_optional */}
                <div className={`hidden col-span-4 aspect-square lg:flex items-center justify-center bg-white p-3 border border-slate-200 rounded-lg max-w-44 max-h-44 ${receipt?.checkoutMode === 'login_optional' ? 'order-2' : 'order-1'}`}>
                  <QRCodeSVG
                    value={APP_STORE_URL}
                    size={160}
                    className="w-full h-full"
                  />
                </div>

                {/* Text content - left for login_required, right for login_optional */}
                <div className={`col-span-8 max-w-80 lg:max-w-none flex flex-col justify-between ${receipt?.checkoutMode === 'login_optional' ? 'order-1' : 'order-2'}`}>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      {receipt?.checkoutMode === 'login_optional' && (
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-slate-50 flex items-center justify-center text-sm">
                          1
                        </div>
                      )}
                      <h3 className="text-base font-semibold md:text-lg text-slate-900">{t('success.download.title')}</h3>
                    </div>
                    <p className="text-sm md:text-base text-slate-600">
                      {t('success.download.description')}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Button
                      className={`bg-yellow-300 text-sm text-slate-800 hover:bg-yellow-400 transition-colors py-5 rounded-lg border-b-4 border-yellow-500 w-full ${downloadClicked ? 'opacity-40' : 'opacity-100'}`}
                      onClick={() => {
                        setDownloadClicked(true);
                        track(EVENTS.CLICK_APP_STORE, {
                          from: 'success_page',
                          sessionId,
                          provider: receipt?.metadata?.login_provider
                        });
                        window.open(APP_STORE_URL, '_blank');
                      }}
                    >
                      {t('success.download.button')}
                    </Button>

                    {/* Mobile QR Code Toggle Button */}
                    <Button
                      variant="outline"
                      className={`lg:hidden w-full text-slate-500 text-sm flex items-center justify-center gap-2 transition-opacity ${downloadClicked ? 'opacity-40' : 'opacity-100'}`}
                      onClick={() => setShowAppStoreQrCode(!showAppStoreQrCode)}
                    >
                      {t('success.download.showQrCode')}
                      <IconChevronDown
                        className={`w-4 h-4 transition-transform ${showAppStoreQrCode ? 'rotate-180' : ''}`}
                      />
                    </Button>

                    {/* Mobile QR Code */}
                    <div className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${showAppStoreQrCode ? 'max-h-[250px] opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="flex items-center justify-center bg-white p-4 border border-slate-200 rounded-lg">
                        <QRCodeSVG
                          value={APP_STORE_URL}
                          size={200}
                          className="w-full h-full max-w-[200px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Separator */}
              {receipt?.checkoutMode === 'login_required' && (
                <div className="flex items-center gap-4 mb-8 mt-4">
                  <div className="h-[1px] flex-1 bg-slate-200"></div>
                  <p className="text-xs md:text-xs whitespace-nowrap text-slate-400">{t('success.steps.title')}</p>
                  <div className="h-[1px] flex-1 bg-slate-200"></div>
                </div>
              )}

              {/* Steps (Login Optional) */}
              {receipt?.checkoutMode === 'login_optional' && (
                <>
                  {/* Separator */}
                  <div className="h-[1px] w-full lg:mb-8 mb-6 lg:mt-4 mt-0 bg-slate-200"></div>

                  {/* Activation */}
                  <div className="grid lg:grid-cols-12 gap-6 items-center mt-4 mb-10">

                    {/* QR Code - positioned on the right for login_optional */}
                    <div className="hidden col-span-4 aspect-square lg:flex items-center justify-center bg-white p-3 border border-slate-200 rounded-lg order-2 max-w-44 max-h-44">
                      <QRCodeSVG
                        value={`clarymind://activate?session_id=${sessionId}`}
                        size={160}
                        className="w-full h-full"
                      />
                    </div>

                    {/* Text content - positioned on the left for login_optional */}
                    <div className="col-span-8 max-w-80 lg:max-w-none flex flex-col justify-between order-1">
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-slate-50 flex items-center justify-center text-sm">
                            2
                          </div>
                          <h3 className="text-base font-semibold md:text-lg text-slate-900">{t('success.activate.title')}</h3>
                        </div>
                        <p className="text-sm md:text-base text-slate-600">
                          {t('success.activate.description')}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Button
                          className={`bg-yellow-300 text-sm text-slate-800 hover:bg-yellow-400 transition-colors py-5 rounded-lg border-b-4 border-yellow-500 w-full ${downloadClicked ? 'opacity-100' : 'opacity-40'}`}
                          onClick={() => {
                            track(EVENTS.CLICK_ACTIVATE_SUBSCRIPTION, {
                              from: 'success_page',
                              sessionId,
                            });
                            window.location.href = `clarymind://activate?session_id=${sessionId}`;
                          }}
                        >
                          {t('success.activate.button')}
                        </Button>

                        {/* Mobile QR Code Toggle Button */}
                        <Button
                          variant="outline"
                          className={`lg:hidden w-full flex text-slate-500 text-sm items-center justify-center gap-2 transition-opacity ${downloadClicked ? 'opacity-100' : 'opacity-40'}`}
                          onClick={() => setShowQrCode(!showQrCode)}
                        >
                          {t('success.activate.showQrCode')}
                          <IconChevronDown
                            className={`w-4 h-4 transition-transform ${showQrCode ? 'rotate-180' : ''}`}
                          />
                        </Button>

                        {/* Mobile QR Code */}
                        <div className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${showQrCode ? 'max-h-[250px] opacity-100' : 'max-h-0 opacity-0'}`}>
                          <div className="flex items-center justify-center bg-white p-4 border border-slate-200 rounded-lg">
                            <QRCodeSVG
                              value={`clarymind://activate?session_id=${sessionId}`}
                              size={200}
                              className="w-full h-full max-w-[200px]"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="h-[1px] w-full lg:mb-8 mb-6 lg:mt-4 mt-0 bg-slate-200"></div>

                  {/* Reopen the app */}
                  <div className="flex items-start gap-4 mt-4 mb-4">
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-slate-50 flex items-center justify-center text-sm">
                          3
                        </div>
                        <h3 className="text-base font-semibold md:text-lg text-slate-900">{t('success.reopen.title')}</h3>
                      </div>
                      <p className="text-sm md:text-base text-slate-600">
                        {t('success.reopen.description')}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Steps (Login Required) */}
              {receipt?.checkoutMode === 'login_required' && (
                <div className="space-y-8">

                  {/* Step 1: Choose Account */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-slate-50 flex items-center justify-center text-sm mt-0.5">
                      1
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium mb-2">{t('success.steps.step1.title')}</h3>
                      <p className="text-muted-foreground mb-4">
                        {t('success.steps.step1.description')}
                      </p>
                      <div className="bg-slate-100 rounded-lg overflow-hidden">
                        <video
                          className="w-full"
                          autoPlay
                          loop
                          muted
                          playsInline
                        >
                          <source src={t('success.loginVideo')} type="video/mp4" />
                        </video>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Step 2: Login */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-slate-50 flex items-center justify-center text-sm mt-0.5">
                      2
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium mb-2">{t('success.steps.step2.title')}</h3>
                      <p className="text-muted-foreground mb-4">
                        {t('success.steps.step2.description')}
                      </p>
                      <Card className="p-4 inline-block">
                        <div className="flex items-center gap-3">
                          <img
                            src={receipt?.metadata?.login_provider === 'apple' ? '/brands/apple.svg' : '/brands/google.svg'}
                            alt={`${receipt?.metadata?.login_provider} logo`}
                            className="w-6 h-6"
                          />
                          <span>{receipt?.metadata?.user_email}</span>
                        </div>
                      </Card>
                    </div>
                  </div>

                  <Separator />

                  {/* Step 3: Restore */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-slate-50 flex items-center justify-center text-sm mt-0.5">
                      3
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium mb-2">{t('success.steps.step3.title')}</h3>
                      <p className="text-muted-foreground mb-4">
                        {t('success.steps.step3.description')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              </Card>
            </div>
        </div>

        {/* Mobile sections - Show help and order info at bottom on small screens */}
        <div className="lg:hidden mt-8 space-y-6">
          {/* Help Section for mobile */}
          <HelpSection />

          {/* Order Information for mobile */}
          <OrderInfoSection receipt={receipt} />
        </div>
      </div>
    </div>
  );
}